import { orderBy } from 'lodash';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StyledTable } from 'components/DataTable/DataTable.style';

import { type TTurbineProps } from '../../types';

import { getTurbineColumns } from './turbineColumns';

// Fleet-wide lists run to thousands of rows; virtualize only long ones since the
// virtual body drops table roles.
const VIRTUAL_ROW_THRESHOLD = 200;

type TurbinesListProps = {
  containerHeight: number | string;
  data: Array<TTurbineProps>;
};

const TurbinesListComponent = ({ containerHeight, data }: TurbinesListProps) => {
  const { i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const isVirtual = data.length > VIRTUAL_ROW_THRESHOLD;
  const [scrollHeight, setScrollHeight] = useState<number | undefined>(undefined);

  // Scroll the body once it outgrows the container. A virtual body always needs a height.
  // `containerHeight` can be a CSS length (e.g. '35rem'), so measure the rendered box.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const headerHeight = container.querySelector('.ant-table-thead')?.clientHeight ?? 0;
    const maxBodyHeight = Math.max(1, container.clientHeight - headerHeight);
    const bodyHeight = container.querySelector('.ant-table-tbody')?.clientHeight ?? 0;
    setScrollHeight(isVirtual || bodyHeight > maxBodyHeight ? maxBodyHeight : undefined);
  }, [containerHeight, data, isVirtual]);

  const sortedData = React.useMemo(() => {
    return orderBy(
      data,
      [
        d => d.inspectionData?.maxSeverity ?? -Infinity,
        d => d.inspectionData?.inspectionDate ?? '',
      ],
      ['desc', 'desc']
    );
  }, [data]);

  // i18n.language so a live language switch refreshes titles; React.memo alone would not.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getTurbineColumns reads i18n.t at call time
  const columns = React.useMemo(() => getTurbineColumns(), [i18n.language]);

  return (
    <div ref={containerRef} style={{ height: containerHeight }}>
      <StyledTable
        columns={columns}
        dataSource={sortedData}
        pagination={false}
        rowKey="key"
        size="middle"
        virtual={isVirtual}
        // Until measured, any positive height keeps a virtual body from rendering every row.
        scroll={
          isVirtual ? { y: scrollHeight ?? 400 } : scrollHeight ? { y: scrollHeight } : undefined
        }
        tableLayout="fixed"
      />
    </div>
  );
};

/*
  This table lists every Turbine on the Site without paging, so re-rendering it alongside
  the map is expensive. The Turbines only change when the map data does.
*/
export const TurbinesList = React.memo(TurbinesListComponent);
